import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  MoreOutlined
} from "@ant-design/icons"
import { App, Button, Card, Empty, Flex, Skeleton, Typography } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate, useParams } from "react-router"
import { Link, Navigate } from "react-router-dom"

import { FixedContainer } from "../../../components/FixedContainer.tsx"
import { RecordRef } from "../../../components/RecordRef.tsx"
import { excludeDeletedAccounts } from "../../../constants/deleted-account-ui.ts"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import type { RootState } from "../../../store.ts"
import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { getEntityName } from "../../../utils/user/get-entity-name"
import { LeadPill } from "../../Leads/components/LeadPill.tsx"
import { getFirstVisibleProjectManagementRoute } from "../helpers/project-management-route.ts"
import {
  projectsViewPath,
  useProjectsView
} from "../../Projects/helpers/projects-view-preference.ts"
import { fetchProjectsFilterOptions } from "../../Projects/redux/projects.thunks.ts"
import { OverviewCardIcon } from "../../Lead/LeadWorkspacePage/overview/OverviewCardIcon.tsx"
import { ProjectForm } from "../ProjectAddPage/components/ProjectForm.tsx"
import { type ProjectFormData } from "../ProjectAddPage/components/project-form-data.ts"
import { ProjectStatusSelector } from "../components/ProjectStatusSelector.tsx"
import { PROJECT_ENTITY_CATEGORIES } from "../ProjectViewPage/constants/project-entity-category.ts"
import { diffProjectTypeSelection } from "../helpers/diff-project-types.ts"
import { getProjectNavigatePath } from "../helpers/get-project-navigate-path.tsx"
import {
  addProjectTypeToProject,
  fetchProject,
  removeProjectTypeToProject,
  updateProject
} from "../redux/project.thunks.ts"
import { type ProjectUpdateData } from "../types/project.types.ts"

const { Title, Text } = Typography

// One rotating pastel accent per Project Type pill — the same palette Lead's
// Overview cards use for their icon circles, cycled by index rather than
// invented per-type, since a type has no color of its own to key off.
const TYPE_PILL_ACCENTS = [
  { color: "#2A7D8C", background: "#E7F3F1" },
  { color: "#16A34A", background: "#ECF9EF" },
  { color: "#FF8A3D", background: "#FFF1E8" },
  { color: "#6366F1", background: "#EEF0FE" },
  { color: "#0284C7", background: "#E6F6FE" }
] as const

export const ProjectEditPage = ({
  user,
  project,
  saving,
  filterOptions,
  projectPermissions,
  updateProject,
  addProjectType,
  removeProjectType,
  fetching,
  fetchFilterOptions,
  getProjectById
}: ConnectedProps<typeof connector>) => {
  const { modal } = App.useApp()
  const navigate = useNavigate()
  const [projectsView] = useProjectsView()
  const id = useParams()?.id as string
  const [submitting, setSubmitting] = useState(false)
  const { formatDate, dateFormat } = useDateFormatter()

  const handleStatusChange = async (project_status: number) => {
    try {
      await updateProject({ id, project_status }).unwrap()
    } catch (e) {
      console.error("Update project status error:", e)
    }
  }

  const options = useMemo(
    () => ({
      state: filterOptions?.state,
      sales_manager: filterOptions?.sales_manager,
      source: filterOptions?.source,
      contact_method: filterOptions?.contact_method,
      project_manager: filterOptions?.project_manager,
      subcontractor: filterOptions?.subcontractor?.map((s) => ({
        label: s?.label,
        value: s?.value?.id
      }))
    }),
    [
      filterOptions?.project_manager,
      filterOptions?.state,
      filterOptions?.contact_method,
      filterOptions?.subcontractor
    ]
  )

  const initialValues: ProjectFormData = useMemo(() => {
    if (!project) return {} as ProjectFormData

    return {
      ...project,
      subcontractors: project?.subcontractors_details?.map((s) => s?.id),
      file_list: [],
      deleted_attachment_ids: [],
      customer: project?.customer || project?.temporary_customer,
      project_managers: excludeDeletedAccounts(project?.project_managers)?.map(
        (p) => ({
          id: p.id,
          is_main: p?.is_main
        })
      ),
      sales_manager: excludeDeletedAccounts(project?.sales_manager)?.map(
        (s) => ({
          id: s.id,
          is_main: s?.is_main
        })
      ),
      activeProjectTypeId: project?.project_type?.[0]
    }
  }, [project])

  const handleUpdateProject = async ({
    project_type_categories,
    ...data
  }: ProjectFormData) => {
    // Diff against project_type_details (kept fresh by the add/remove reducers),
    // not project_type (only refreshed on a full details PATCH) — otherwise a
    // retry after a mid-sequence failure re-adds an already-assigned type.
    const { addedIds, removedIds } = diffProjectTypeSelection(
      project?.project_type_details?.map((t) => t.id),
      data?.project_type
    )

    const persist = async () => {
      setSubmitting(true)
      try {
        // Add before remove so the backend's "keep at least one type" guard
        // never trips while swapping a project's only type.
        for (const project_type_id of addedIds) {
          await addProjectType({
            projectId: id,
            data: {
              project_type_id,
              category:
                project_type_categories?.[project_type_id] ??
                PROJECT_ENTITY_CATEGORIES.STANDARD
            }
          }).unwrap()
        }

        for (const project_type_id of removedIds) {
          await removeProjectType({ projectId: id, project_type_id }).unwrap()
        }

        const updatedProject = await updateProject({
          id,
          ...data
        } as ProjectUpdateData).unwrap()
        navigate(
          getProjectNavigatePath(
            updatedProject,
            user?.id,
            user?.role?.id,
            projectsViewPath(projectsView)
          )
        )
      } catch (e) {
        console.error("Update project error:", e)
      } finally {
        setSubmitting(false)
      }
    }

    if (removedIds.length) {
      modal.confirm({
        title: "Remove Project Type",
        content: (
          <>
            <p>
              You&apos;re removing{" "}
              {removedIds.length === 1 ? "a project type" : "project types"}{" "}
              from this project.
            </p>
            <p>
              All related milestones, financial items and tasks will be deleted
              as well.
            </p>
          </>
        ),
        okText: "Remove",
        okType: "danger",
        cancelText: "Cancel",
        onOk: persist
      })
      return
    }

    await persist()
  }

  useEffect(() => {
    if (id) getProjectById(id)
  }, [id, getProjectById])

  useEffect(() => {
    generateDocumentTitle("Edit Project")
    fetchFilterOptions()
  }, [])

  if (fetching) return <Skeleton.Node className={"size-full"} />

  if (!project) {
    return (
      <Card size={"small"}>
        <Empty description={"Project not found"} />
      </Card>
    )
  }

  // This route is outside the project layout, so hiding the edit affordances
  // does not stop a bookmark reaching a form the API will refuse to save.
  if (project.is_archived) {
    return <Navigate replace to={RoutesPaths.project.view(id).overview} />
  }

  const customer = project?.customer || project?.temporary_customer
  const typePills = project?.project_type_details?.map((t) => t.name) ?? []
  const managementRoute = getFirstVisibleProjectManagementRoute(
    projectPermissions,
    user?.role?.id,
    id
  )

  return (
    <FixedContainer>
      <div className={"grid"}>
        <Flex
          vertical
          gap={"small"}
          className={"max-h-[calc(100vh-90px)] overflow-auto"}
        >
          <Card size={"small"} styles={{ body: { padding: "16px 20px" } }}>
            <Flex vertical gap={4}>
              <Link to={RoutesPaths.project.list}>
                <Button
                  type={"text"}
                  size={"small"}
                  icon={<ArrowLeftOutlined />}
                  className={"!px-1 !text-secondary"}
                >
                  Back to Projects
                </Button>
              </Link>

              <Flex justify={"space-between"} align={"start"} gap={"middle"} wrap>
                <Flex gap={"middle"} align={"center"} className={"min-w-0"}>
                  <OverviewCardIcon
                    icon={<HomeOutlined />}
                    accent={"teal"}
                    size={56}
                  />
                  <Flex vertical gap={2} className={"min-w-0"}>
                    <RecordRef
                      uid={project?.uid}
                      customerName={
                        customer ? getEntityName(customer) : undefined
                      }
                    />
                    <Flex align={"center"} gap={8} wrap>
                      <Title
                        level={3}
                        className={"m-0"}
                        ellipsis={{ tooltip: project?.lead_name }}
                      >
                        {project?.lead_name || "No project name"}
                      </Title>
                      <ProjectStatusSelector
                        status={project?.project_status}
                        statuses={filterOptions?.project_status || []}
                        onChange={handleStatusChange}
                      />
                      {typePills.map((name, index) => (
                        <LeadPill
                          key={name}
                          style={{
                            color:
                              TYPE_PILL_ACCENTS[index % TYPE_PILL_ACCENTS.length]
                                .color,
                            background:
                              TYPE_PILL_ACCENTS[index % TYPE_PILL_ACCENTS.length]
                                .background,
                            border: "none"
                          }}
                        >
                          {name}
                        </LeadPill>
                      ))}
                    </Flex>
                    <Flex gap={8} wrap>
                      {customer && (
                        <Text type={"secondary"}>
                          Customer: <Text strong>{getEntityName(customer)}</Text>
                        </Text>
                      )}
                      {project?.created && (
                        <Text type={"secondary"}>
                          Created {formatDate(project.created, dateFormat)}
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                </Flex>

                <Flex gap={"small"} align={"center"}>
                  {/* This screen is itself the View/Edit experience — each
                      section edits in place, so there is no separate "Edit
                      Project" button. Project Management (Milestones,
                      Finance, Invoices, ...) is the other, deliberate
                      destination. */}
                  {managementRoute ? (
                    <Link to={managementRoute}>
                      <Button type={"primary"} icon={<AppstoreOutlined />}>
                        Go to Project Management
                      </Button>
                    </Link>
                  ) : (
                    <Button type={"primary"} icon={<AppstoreOutlined />} disabled>
                      Go to Project Management
                    </Button>
                  )}
                  {/* Reserved for future actions (e.g. Archive) — none are
                      part of this step. */}
                  <Button icon={<MoreOutlined />} aria-label={"More actions"} disabled />
                </Flex>
              </Flex>
            </Flex>
          </Card>
          <ProjectForm
            isEdit
            saving={saving || submitting}
            options={options}
            initialValues={initialValues}
            projectFull={project}
            onSubmit={handleUpdateProject}
          />
        </Flex>
      </div>
    </FixedContainer>
  )
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth?.userInfo,
  fetching: state.project.fetching || state.projects.fetchingFilterOptions,
  project: state.project.project,
  saving: state.project.saveFetching,
  filterOptions: state.projects.filterOptions,
  projectPermissions: state.auth.permissions?.codes?.projects
})

const mapDispatchToProps = {
  getProjectById: fetchProject,
  updateProject,
  addProjectType: addProjectTypeToProject,
  removeProjectType: removeProjectTypeToProject,
  fetchFilterOptions: fetchProjectsFilterOptions
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectEditPage)
